import { mapValues } from 'lodash'
import moment from 'moment/moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useRerenderOnMutation_deprecated } from '../../hooks/useRerenderOnMutation'
import BarChartSwitchIcon from '../../icons/ps_reporting_barchart_switch.svg?react'
import HeatMapSwitchIcon from '../../icons/ps_reporting_heatmap_switch.svg?react'
import MoreIcon from '../../icons/ps_reporting_more.svg?react'
import SaveIcon from '../../icons/ps_reporting_save.svg?react'
import { BaseRecord } from '../../models/BaseRecord'
import { Feature } from '../../models/Feature'
import { Option } from '../../models/Option'
import { PSComponent } from '../../models/PSComponent'
import { RecordFactory } from '../../models/RecordFactory'
import { Report } from '../../models/Report'
import { ReportResult } from '../../models/ReportResult'
import { ReportTotal } from '../../models/ReportTotal'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { reportingColors } from '../../utils/colors'
import {
    SESSION_STORAGE_REPORT_STATE_KEY,
    SESSION_STORAGE_RETURNED_KEY,
} from '../../utils/constants'
import { ComponentData } from '../../utils/typing'
import { PSVisualizationDataType, VisualizationType } from '../../utils/visualization'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { AppHeader } from '../app/AppHeader'
import Button from '../app/design/Button'
import SegmentedControl from '../app/design/SegmentedControl'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { Select } from '../common/fields/Select'
import { BarChart, BarChartProps } from './visualization/BarChart'
import { BarChartBinType } from './visualization/BarChartRenderer'
import { HeatMap, HeatMapProps } from './visualization/HeatMap'
import {
    ReportDataTable,
    TableColumnsType,
    TableRowSortedKeysType,
    TableRowsType,
    TableSubrowsType,
} from './ReportDataTable'
import { ReportDetailsLocationProps } from './ReportDetailsPage'
import { ReportMetricsModal } from './ReportMetricsModal'
import { ReportMetricsPage } from './ReportMetricsPage'
import { ReportNameModal, ReportNameModalType } from './ReportNameModal'
import { ReportSelectDateModal } from './ReportSelectDateModal'
import { ReportSelectMetricModal } from './ReportSelectMetricModal'
import { ReportSelectModal } from './ReportSelectModal'

type ReportRunnerPageProps = {
    // todo: currently subaccount is not supported.
    //  We might want to pass it in the url. (see how person is implemented)
    //  Also make sure how to save the state if subaccount is defined, we might not expect the navbar link to restore the report for another subaccount
    //  Also in some places the subaccount is passed in locationState, e.g. onDelete, figure out how to handle that
    subaccount?: Subaccount
    loadDefaultReport?: boolean
}

type ButtonChipsData = {
    title: string
    selected: boolean
}

type ReportRunnerPageData = {
    reports: Report[]
    reportDimensions: PSComponent[]
    activeReport: Report
    person?: User
    subaccount?: Subaccount
    reportState?: ReportStateProps
    returned: boolean
}

type TableDataType = {
    columns: TableColumnsType
    rows: TableRowsType
    subrows: TableSubrowsType
    sorting: TableRowSortedKeysType
    groupByEligible: boolean
}

type ReportStateProps = {
    activeReport: Report
    activeVisualization: VisualizationType
    activeMetric: PSComponent
    barChartData: BarChartProps
    heatMapData: HeatMapProps
    tableData: TableDataType
    subaccount?: Subaccount
}

export type ShowReportDetailsProps = {
    selectedMeasure: PSComponent
    measureTotal: ReportTotal
    values: ReportResult[]
}

function saveReportState(props: ReportStateProps) {
    const serializedTableData = {
        columns: mapValues(props.tableData.columns, (r) => r.serialize()),
        rows: mapValues(props.tableData.rows, (r) => r.map((s) => s.serialize())),
        subrows: mapValues(props.tableData.subrows, (r) =>
            r.map((s) => s.map((t) => t.serialize()))
        ),
        sorting: props.tableData.sorting,
        groupByEligible: props.tableData.groupByEligible,
    }

    const state = {
        activeReport: props.activeReport.serialize(),
        activeVisualization: props.activeVisualization,
        activeMetric: props.activeMetric.serialize(),
        barChartData: {
            ...props.barChartData,
            labels: props.barChartData.labels.map((l) => l.toISOString(true)),
            fromDate: props.barChartData.fromDate?.toISOString(true),
            toDate: props.barChartData.toDate?.toISOString(true),
        },
        heatMapData: props.heatMapData,
        tableData: serializedTableData,
        subaccount: props.subaccount?.serialize(),
    }

    sessionStorage.setItem(SESSION_STORAGE_REPORT_STATE_KEY, JSON.stringify(state))
}

function loadReportState(): ReportStateProps | undefined {
    if (!sessionStorage.getItem(SESSION_STORAGE_REPORT_STATE_KEY)) {
        return undefined
    }

    const state = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_REPORT_STATE_KEY)!)

    const deserializedTableData = {
        columns: mapValues(state.tableData.columns, (r: any) =>
            BaseRecord.fromSerializer(r, ReportTotal)
        ),
        rows: mapValues(state.tableData.rows, (r: any) =>
            r.map((s: any) => BaseRecord.fromSerializer(s, ReportResult))
        ),
        subrows: mapValues(state.tableData.subrows, (r: any) =>
            r.map((s: any) => s.map((t: any) => BaseRecord.fromSerializer(t, ReportResult)))
        ),
        sorting: state.tableData.sorting,
        groupByEligible: state.tableData.groupByEligible,
    }

    return {
        activeReport: BaseRecord.fromSerializer(state.activeReport, Report),
        activeVisualization: state.activeVisualization,
        activeMetric: BaseRecord.fromSerializer(state.activeMetric, PSComponent),
        barChartData: {
            ...state.barChartData,
            labels: state.barChartData.labels.map((l: string) => moment(l)),
            fromDate: state.barChartData.fromDate ? moment(state.barChartData.fromDate) : null,
            toDate: state.barChartData.toDate ? moment(state.barChartData.toDate) : null,
        },
        heatMapData: state.heatMapData,
        tableData: deserializedTableData,
        subaccount: state.subaccount
            ? BaseRecord.fromSerializer(state.subaccount, Subaccount)
            : undefined,
    }
}

export const REPORTS_PATH = '/reports'

const viewWillAppear = async (base: PocketComponent<ReportRunnerPageProps>) => {
    // TODO: replace with proper error handling for unauthorized access
    if (base.me.payee) {
        const payee = await RecordFactory.getInstance().fetchPayee(base.me.id ?? '', false)
        if (!payee?.showIncome) {
            return new RecordNotFoundError('Report not found')
        }
    }

    const clientId = base.searchParams.get('clientId') ?? undefined
    const person = clientId ? await RecordFactory.getInstance().loadUser(clientId) : undefined

    const componentSync = await RecordFactory.getInstance().fetchComponent(undefined, undefined) // sync components
    if (!componentSync.ok) {
        base.logAndDisplayError(componentSync.error)
        return new RecordNotFoundError('Component not found')
    }

    const reports = await RecordFactory.getInstance().loadReports()
    const reportDimensions = await RecordFactory.getInstance().loadComponents('dimension')

    const returned = sessionStorage.getItem(SESSION_STORAGE_RETURNED_KEY) === 'true'
    // Problem: we don't want to load the report from the saved state if the page is refreshed
    // Solution: returned flag will be removed from the sessionStorage in the component body

    const loadDefaultReport = base.props.loadDefaultReport ?? base.locationState?.loadDefaultReport

    const tempReportState = loadReportState()
    const restore =
        !!tempReportState && (returned || (!loadDefaultReport && !base.urlParams.reportId))
    const reportState = restore ? tempReportState : undefined

    let activeReport = undefined

    if (!reportState) {
        const reportId =
            base.urlParams.reportId ??
            (base.me.plan !== 'franchise' ? 'report_income' : 'report_rollup_sales')
        const defaultReport = reports.find((r) => r.id === reportId)
        if (defaultReport === undefined) {
            return new RecordNotFoundError('Report not found')
        }
        activeReport = defaultReport.clone()
    }
    else {
        activeReport = reportState.activeReport
    }

    return {
        reports: reports,
        reportDimensions: reportDimensions,
        activeReport: activeReport,
        person,
        subaccount: reportState?.subaccount ?? base.props.subaccount,
        reportState: reportState,
        returned: returned,
    }
}

const render = ({
    data,
    base,
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, ReportRunnerPageProps>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    usePageTitle('Reports')

    const reportData = data as ReportRunnerPageData

    if (sessionStorage.getItem(SESSION_STORAGE_RETURNED_KEY) === 'true') {
        // Don't load from saved state if returned from details page and the component is refreshed
        sessionStorage.removeItem(SESSION_STORAGE_RETURNED_KEY)
    }

    const activeReportProxy = useRerenderOnMutation_deprecated(reportData.activeReport)

    const navigate = usePSNavigate()

    const [reports, setReports] = useState<Report[]>(reportData.reports)

    const [dateButtonState, setDateButtonState] = useState<ButtonChipsData>({
        title: '',
        selected: false,
    })

    const [comparisonButtonState, setComparisonButtonState] = useState<ButtonChipsData>({
        title: '',
        selected: false,
    })

    const [group1ButtonState, setGroup1ButtonState] = useState<ButtonChipsData>({
        title: '',
        selected: false,
    })

    const [group2ButtonState, setGroup2ButtonState] = useState<ButtonChipsData>({
        title: '',
        selected: false,
    })

    const [saveDropdownOpen, setSaveDropdownOpen] = useState(false)
    const [moreDropdownOpen, setMoreDropdownOpen] = useState(false)

    const [activeVisualizationState, setActiveVisualizationState] = useState<VisualizationType>(
        reportData.reportState?.activeVisualization ?? VisualizationType.BAR_CHART
    )

    const [barChartDataState, setBarChartDataState] = useState<BarChartProps>(
        reportData.reportState?.barChartData ?? {
            values: [],
            labels: [],
            bin: BarChartBinType.MONTH,
            fromDate: null,
            toDate: null,
            color: reportingColors.blue400,
            dataType: PSVisualizationDataType.INTEGER,
        }
    )

    const [heatMapDataState, setHeatMapDataState] = useState<HeatMapProps>(
        reportData.reportState?.heatMapData ?? {
            values: [],
            labels: [],
            color: reportingColors.blue400,
            dataType: PSVisualizationDataType.INTEGER,
        }
    )

    const [tableDataState, setTableDataState] = useState<TableDataType>(
        reportData.reportState?.tableData ?? {
            columns: {},
            rows: {},
            subrows: {},
            sorting: { results: [], subresults: [] },
            groupByEligible: false,
        }
    )

    const [activeMetricState, setActiveMetricState] = useState<PSComponent>(
        reportData.reportState?.activeMetric ?? activeReportProxy.measures[0]
    )

    const [showGuardrail, setShowGuardrail] = useState<undefined | 'delete'>(undefined)
    const guardrailComponent = useMemo(() => {
        if (showGuardrail !== undefined) {
            return (
                <ConfirmationModal
                    isOpen
                    isDestructive
                    title="Are you sure?"
                    children="If you remove this report, you will no longer see it in the reports page."
                    confirmButtonText="Delete"
                    onClose={() => {
                        setShowGuardrail(undefined)
                    }}
                    onConfirm={() => {
                        setShowGuardrail(undefined)
                        deleteReport()
                    }}
                />
            )
        }
    }, [showGuardrail])

    const [activeModal, setActiveModal] = useState<React.ReactNode | undefined>(undefined)

    const dontCallFetch = useRef<boolean>(reportData.returned)

    useEffect(() => {
        if (dontCallFetch.current) {
            didUpdateReport()
            dontCallFetch.current = false
        }
        else {
            fetchData()
        }
    }, [activeMetricState]) // only call fetchData the first time the React component runs and when activeMetricState changes

    useEffect(() => {
        if (Object.keys(tableDataState.columns).length === 0) {
            // don't save the state if the table is not initialized (first render)
            return
        }

        saveReportState({
            activeReport: activeReportProxy,
            activeVisualization: activeVisualizationState,
            activeMetric: activeMetricState,
            barChartData: barChartDataState,
            heatMapData: heatMapDataState,
            tableData: tableDataState,
            subaccount: reportData.subaccount,
        })
    }, [
        activeVisualizationState,
        activeMetricState,
        barChartDataState,
        heatMapDataState,
        tableDataState,
    ])

    async function fetchData() {
        base.showProgressBar('Loading ..')
        const result = await RecordFactory.getInstance().runReport(
            activeMetricState.id!,
            activeReportProxy.measures,
            activeReportProxy.dimensions,
            activeReportProxy.criterias,
            activeReportProxy.date,
            activeReportProxy.endDate,
            activeReportProxy.dateType,
            activeReportProxy.dateCompareType,
            reportData.person,
            reportData.subaccount
        )
        if (result.ok) {
            const results = result.value
            activeReportProxy.date = results['date']
            activeReportProxy.endDate = results['end_date']
            activeReportProxy.dateType = results['date_type']
            activeReportProxy.dateCompareType = results['date_compare_type']
            const rowDataByMeasure = results['total_by_type'] ?? {}
            const rowSortingByMeasure = results['sort_by_type'] ?? {
                results: [],
                subresults: [],
            }
            loadTableData(results['total'], rowDataByMeasure, rowSortingByMeasure)
            loadChartData(results['total_by_date'], results['total_by_dow'])
            didUpdateReport()
            base.hideProgressBar()
        }
        else {
            base.hideProgressBar()
            base.logAndDisplayError(result.error)
        }
    }

    function loadChartData(
        incomeByDate: { [key: string]: any },
        incomeByDow: { [key: string]: any }
    ) {
        const ix = activeReportProxy.measures.findIndex((m) => m.id === activeMetricState.id)
        let dataType: PSVisualizationDataType = PSVisualizationDataType.INTEGER
        if (activeReportProxy.measures[ix].fieldType === 'currency') {
            dataType = PSVisualizationDataType.CURRENCY
        }
        else if (activeReportProxy.measures[ix].fieldType === 'percent') {
            dataType = PSVisualizationDataType.PERCENT
        }

        const color =
            ix === 3
                ? 'rgb(137, 108, 194)'
                : ix === 2
                  ? 'rgb(60, 148, 168)'
                  : ix === 1
                    ? 'rgb(240, 152, 73)'
                    : /* PRIMARY_BLUE */ 'rgb(61, 132, 246)'

        const chartX: moment.Moment[] = []
        const chartY: number[] = []

        let income = incomeByDate[activeMetricState.id!]
        let labels = income['keys']
        let totals = income['totals']

        if (labels.length !== totals.length) {
            base.logAndDisplayError('Data size mismatch')
            return
        }

        for (let i = 0; i < labels.length; i++) {
            const date = labels[i]
            const value = totals[i]
            if (date && value) {
                chartX.push(date)
                chartY.push(value.total!)
            }
        }

        const binString = income['date_bin'].toUpperCase()
        const binType =
            binString === 'YEAR'
                ? BarChartBinType.YEAR
                : binString === 'QUARTER'
                  ? BarChartBinType.QUARTER
                  : binString === 'MONTH'
                    ? BarChartBinType.MONTH
                    : binString === 'WEEK'
                      ? BarChartBinType.WEEK
                      : BarChartBinType.DAY

        setBarChartDataState({
            values: chartY,
            labels: chartX,
            bin: binType,
            fromDate: activeReportProxy.date!,
            toDate: activeReportProxy.endDate!,
            color: color,
            dataType: dataType,
        })

        income = incomeByDow[activeMetricState.id!]
        labels = income['keys']
        totals = income['totals']
        const values: { [key: string]: number }[] = []

        if (labels.length !== totals.length) {
            base.logAndDisplayError('Data size mismatch')
            return
        }

        for (const value of totals) {
            const items: { [key: string]: number } = {}
            const keys = Object.keys(value)
            for (const key of keys) {
                items[key] = value[key].total!
            }
            values.push(items)
        }

        setHeatMapDataState({
            values: values,
            labels: labels,
            color: color,
            dataType: dataType,
        })
    }

    function loadTableData(
        totalByMeasure: TableColumnsType,
        rowDataByMeasure: { [key: string]: any },
        rowSortingByMeasure: TableRowSortedKeysType
    ) {
        const tableResults: TableRowsType = {}
        const tableSubresults: TableSubrowsType = {}
        const tableSorting: TableRowSortedKeysType = { results: [], subresults: [] }
        const hasSecondaryRow = activeReportProxy.dimensions.length > 1
        const allMeasuresEligibleWithGroupBy = activeReportProxy.measures.every(
            isMeasureEligibleWithGroupBy
        )
        if (allMeasuresEligibleWithGroupBy) {
            for (const measure of activeReportProxy.measures) {
                tableResults[measure.id!] = rowDataByMeasure[measure.id!]?.results ?? []
                tableSubresults[measure.id!] =
                    (hasSecondaryRow
                        ? rowDataByMeasure[measure.id!]?.subresults?.map((x: any) => x?.results)
                        : undefined) ?? []
            }
            tableSorting.results = rowSortingByMeasure.results
            tableSorting.subresults = rowSortingByMeasure.subresults
        }
        else {
            tableSorting.results = []
            tableSorting.subresults = []
        }
        setTableDataState({
            columns: totalByMeasure,
            rows: tableResults,
            subrows: tableSubresults,
            sorting: tableSorting,
            groupByEligible: allMeasuresEligibleWithGroupBy,
        })
    }

    function isMeasureEligibleWithGroupBy(measure: PSComponent) {
        const allowedGroupBy = measure.dimensions
        const firstDimension = activeReportProxy.dimensions.length
            ? activeReportProxy.dimensions[0]
            : undefined
        const secondDimension =
            activeReportProxy.dimensions.length > 1 ? activeReportProxy.dimensions[1] : undefined

        let primaryAllowed = false
        let secondaryAllowed = secondDimension === undefined

        for (const component of allowedGroupBy) {
            if (firstDimension && component.id === firstDimension.id) primaryAllowed = true
            else if (secondDimension && component.id === secondDimension.id) secondaryAllowed = true

            if (primaryAllowed && secondaryAllowed) break
        }

        return primaryAllowed && secondaryAllowed
    }

    const selectReport = useCallback(() => {
        const l = []
        for (const report of reports) {
            if (!report.feature || Feature.enabled(report.feature.feature, base.owner))
                l.push(new Option(undefined, { id: report.id, label: report.title }))
        }

        const onSelect = (option: Option) => {
            navigate(`${REPORTS_PATH}/${option.id}`, {
                state: { subaccount: reportData.subaccount },
            })
        }

        const onClose = () => {
            setActiveModal(undefined)
        }

        setActiveModal(
            <ReportSelectModal
                title={'Reports'}
                selectedValue={activeReportProxy.id}
                selectValues={l}
                onSelect={onSelect}
                onClose={onClose}
            />
        )
    }, [reports])

    function selectGroupBy(primary: boolean) {
        const options: Option[] = []
        const notAllowedOptions: Option[] = []
        const firstDimension = activeReportProxy.dimensions.length
            ? activeReportProxy.dimensions[0]
            : undefined
        const secondDimension =
            activeReportProxy.dimensions.length > 1 ? activeReportProxy.dimensions[1] : undefined

        const allowedDimensions = activeReportProxy.measures
            .map((x) => x.dimensions)
            .reduce((a, b) => a.filter((ax) => b.some((bx) => bx.id === ax.id)))

        if (!primary) options.push(new Option(undefined, { id: '', label: 'None' }))
        for (const component of reportData.reportDimensions) {
            const notSelected =
                (primary &&
                    (secondDimension === undefined || secondDimension.id !== component.id)) ||
                (!primary && (firstDimension === undefined || firstDimension.id !== component.id))
            if (
                notSelected &&
                (!component.feature || Feature.enabled(component.feature.feature, base.owner))
            ) {
                let eligible = false
                for (const d of allowedDimensions) {
                    eligible = d.id === component.id
                    if (eligible) break
                }
                if (eligible)
                    options.push(
                        new Option(undefined, {
                            id: component.id,
                            label: component.title,
                            group: undefined,
                            subtitle: component.memo,
                        })
                    )
                else
                    notAllowedOptions.push(
                        new Option(undefined, {
                            id: component.id,
                            label: component.title,
                            group: 'Not eligible',
                            subtitle: component.memo,
                            enabled: false,
                        })
                    )
            }
        }
        options.push(...notAllowedOptions)

        let groupBy = undefined
        if (activeReportProxy.dimensions.length)
            groupBy = primary
                ? activeReportProxy.dimensions[0].id
                : activeReportProxy.dimensions.length > 1
                  ? activeReportProxy.dimensions[1].id
                  : undefined

        const onSelect = async (option: Option) => {
            let primaryId, secondaryId
            if (primary) {
                primaryId = option.id
                secondaryId =
                    activeReportProxy.dimensions.length > 1
                        ? activeReportProxy.dimensions[1].id
                        : undefined
            }
            else {
                primaryId = activeReportProxy.dimensions[0].id
                secondaryId = option.id
            }

            const components: PSComponent[] = []
            components.push((await RecordFactory.getInstance().loadComponent(primaryId!))!)
            if (secondaryId)
                components.push((await RecordFactory.getInstance().loadComponent(secondaryId))!)
            activeReportProxy.dimensions = components
            setActiveModal(undefined)
            fetchData()
        }

        const onClose = () => {
            setActiveModal(undefined)
        }

        setActiveModal(
            <ReportSelectModal
                title={'Edit dimension'}
                selectedValue={groupBy}
                selectValues={options}
                onSelect={onSelect}
                onClose={onClose}
            />
        )
    }

    function selectTimeComparison() {
        const l = Report.timeComparisonStrings()
        const report = activeReportProxy.dateCompareType
        const callback = (option: Option) => {
            activeReportProxy.dateCompareType = option.id
            fetchData()
        }

        const onSelect = (option: Option) => {
            callback(option)
            setActiveModal(undefined)
        }

        const onClose = () => {
            setActiveModal(undefined)
        }

        setActiveModal(
            <ReportSelectModal
                title={'Select comparison method'}
                selectValues={l}
                selectedValue={report}
                onSelect={onSelect}
                onClose={onClose}
            />
        )
    }

    function selectDate() {
        const onSelect = (option: Option) => {
            if (option.id === 'custom') {
                const onDone = (startDate: moment.Moment, endDate: moment.Moment) => {
                    activeReportProxy.dateType = option.id
                    activeReportProxy.date = startDate
                    activeReportProxy.endDate = endDate
                    setActiveModal(undefined)
                    fetchData()
                }

                const onClose = () => {
                    setActiveModal(undefined)
                }

                setActiveModal(
                    <ReportSelectDateModal
                        startDate={activeReportProxy.date}
                        endDate={activeReportProxy.endDate}
                        onDone={onDone}
                        onClose={onClose}
                        onBack={selectDate}
                    />
                )
            }
            else {
                activeReportProxy.dateType = option.id
                setActiveModal(undefined)
                fetchData()
            }
        }

        const onClose = () => {
            setActiveModal(undefined)
        }

        setActiveModal(
            <ReportSelectModal
                title={'Select date'}
                selectValues={Report.dateRangeStrings()}
                selectedValue={activeReportProxy.dateType}
                onSelect={onSelect}
                onClose={onClose}
            />
        )
    }

    function editMetrics() {
        const onClose = (metrics: PSComponent[]) => {
            const isSame =
                metrics.length === activeReportProxy.measures.length &&
                metrics.every((metric, index) => metric.id === activeReportProxy.measures[index].id)

            if (isSame) {
                setActiveModal(undefined)
                return
            }

            activeReportProxy.measures = metrics
            let foundActiveMeasure = false
            for (const component of activeReportProxy.measures) {
                if (component.id === activeMetricState.id) {
                    foundActiveMeasure = true
                    break
                }
            }
            if (!foundActiveMeasure) {
                setActiveMetricState(activeReportProxy.measures[0])
            }
            else {
                fetchData()
            }
            setActiveModal(undefined)
        }

        setActiveModal(
            <ReportMetricsModal
                metrics={activeReportProxy.measures.map((x) => x.clone())}
                onClose={onClose}
            />
        )
    }

    async function addMetric() {
        if (activeReportProxy.measures.length >= 4) {
            return
        }
        const onSelect = async (option: Option) => {
            const metric = (await RecordFactory.getInstance().loadComponent(option.id))!
            activeReportProxy.measures.push(metric)
            setActiveMetricState(metric)
            setActiveModal(undefined)
        }
        const onClose = () => {
            setActiveModal(undefined)
        }
        setActiveModal(
            <ReportSelectMetricModal
                selected={undefined}
                metricData={activeReportProxy.measures}
                onSelect={onSelect}
                onClose={onClose}
            />
        )
    }

    function didUpdateReport() {
        setDateButtonState({
            title: activeReportProxy.dateDisplayString ?? 'Select interval',
            selected: !!activeReportProxy.dateDisplayString,
        })

        const comparisonString = Report.timeComparisonStrings().find(
            (option) => option.id === activeReportProxy.dateCompareType
        )?.label

        setComparisonButtonState({
            title: comparisonString ?? 'Select comparison method',
            selected: !!comparisonString,
        })

        let groupByString, groupBySecondaryString
        for (const component of reportData.reportDimensions) {
            if (
                activeReportProxy.dimensions.length > 0 &&
                activeReportProxy.dimensions[0].id === component.id
            ) {
                groupByString = component.title
            }
            if (
                activeReportProxy.dimensions.length > 1 &&
                activeReportProxy.dimensions[1].id === component.id
            ) {
                groupBySecondaryString = component.title
            }
        }

        setGroup1ButtonState({
            title: groupByString ?? 'Group',
            selected: !!groupByString,
        })

        setGroup2ButtonState({
            title: groupBySecondaryString ?? 'Group',
            selected: !!groupBySecondaryString,
        })
    }

    const showReportDetails = useCallback(
        (props: ShowReportDetailsProps) => {
            const state: ReportDetailsLocationProps = {
                activeReport: activeReportProxy,
                measure: props.selectedMeasure,
                measureTotal: props.measureTotal,
                values: props.values,
                person: reportData.person,
                subaccount: reportData.subaccount,
            }
            navigate(`${REPORTS_PATH}/${activeReportProxy.id}/details`, { state: state })
        },
        [tableDataState] // refresh the function everytime the fetchData is called (which updates the tableDataState) to ensure the latest activeReportProxy is passed to the details page
    )

    function editName(name?: string) {
        const isCreatingNewReport = name === undefined
        const onDone = (name: string) => {
            activeReportProxy.title = name
            setActiveModal(undefined)
            saveReport(isCreatingNewReport)
        }
        const onClose = () => {
            setActiveModal(undefined)
        }
        const modalType = isCreatingNewReport
            ? ReportNameModalType.SaveAs
            : ReportNameModalType.Rename
        setActiveModal(
            <ReportNameModal
                type={modalType}
                initialName={name}
                onDone={onDone}
                onClose={onClose}
            />
        )
    }

    async function saveReport(isCreatingNewReport: boolean) {
        base.showProgressBar('Saving ..')
        const result = await RecordFactory.getInstance().sendReport(
            activeReportProxy,
            isCreatingNewReport
        )
        setReports(await RecordFactory.getInstance().loadReports())
        base.hideProgressBar()
        if (result.ok) {
            const savedReport = result.value
            if (savedReport?.id !== activeReportProxy.id) {
                const path = `${REPORTS_PATH}/${savedReport.id}`
                navigate(reportData.person ? `${path}?clientId=${reportData.person.id}` : path, {
                    state: { subaccount: reportData.subaccount },
                })
            }
        }
        else {
            base.logAndDisplayError(result.error)
        }
    }

    async function deleteReport() {
        base.showProgressBar('Deleting ..')
        const result = await RecordFactory.getInstance().deleteReport(activeReportProxy.id!)
        base.hideProgressBar()
        if (result.ok) {
            const path = REPORTS_PATH
            navigate(reportData.person ? `${path}?clientId=${reportData.person.id}` : path, {
                state: {
                    loadDefaultReport: true,
                    subaccount: reportData.subaccount,
                },
            })
        }
        else {
            base.logAndDisplayError(result.error)
        }
    }

    function onSaveButton(id: string) {
        if (id === 'save') {
            saveReport(false)
        }
        else if (id === 'save_as') {
            editName()
        }
    }

    function onMoreButton(id: string) {
        if (id === 'rename') {
            editName(activeReportProxy.title)
        }
        else if (id === 'delete') {
            setShowGuardrail('delete')
        }
    }

    const saveOptions = useMemo(() => {
        const l = []
        if (activeReportProxy.isCustom) {
            l.push({ id: 'save', title: 'Save' })
        }
        l.push({ id: 'save_as', title: 'Save as new report' })
        return l
    }, [])

    const moreOptions = useMemo(() => {
        return [
            { id: 'rename', title: 'Rename report' },
            { id: 'delete', title: 'Delete report', isDanger: true },
        ]
    }, [])

    const timeComparisonEnabled = useCallback(() => {
        return activeReportProxy.dateType !== 'custom'
    }, [activeReportProxy.dateType])

    const checkRecordPerm = useCallback(() => {
        return !base.me.payee || base.me.role === 'admin'
    }, [])

    const onBack = useCallback(() => {
        const path = '/'
        navigate(reportData.person ? `${path}?clientId=${reportData.person.id}` : path)
    }, [navigate])

    useEffect(() => {
        window.addEventListener('popstate', onBack)
        return () => {
            window.removeEventListener('popstate', onBack)
        }
    }, [onBack])

    return (
        <AppHeader title="Reports" showBackButton={true} middleWidget={null} onBack={onBack}>
            <div className="reporting">
                <div className="header">
                    <div className="titleGroup">
                        <div className="text-heading-sm font-bold whitespace-nowrap text-gray-700">
                            {activeReportProxy.title}
                        </div>
                    </div>
                    <div className="buttonGroup">
                        <Button
                            className="button"
                            onClick={selectReport}
                            intent="secondary"
                            rightIcon="chevronDown.medium"
                        >
                            {activeReportProxy.title}
                        </Button>
                        {activeReportProxy.isCustom && (
                            <Select
                                size="medium"
                                trigger={
                                    <Button
                                        className="button"
                                        disabled={!checkRecordPerm()}
                                        intent="secondary"
                                        leftIcon={
                                            <MoreIcon style={{ height: '20px' }} color="white" />
                                        }
                                        rightIcon={
                                            <Icon
                                                icon="chevronDown.medium"
                                                className={`transition-transform duration-200 ${moreDropdownOpen ? '-rotate-180' : 'rotate-0'}`}
                                            />
                                        }
                                    >
                                        More
                                    </Button>
                                }
                                options={moreOptions}
                                onOpenChange={() => setMoreDropdownOpen((prev) => !prev)}
                                onSelectOption={(option) => onMoreButton(option.id)}
                            />
                        )}
                        <Select
                            size="medium"
                            trigger={
                                <Button
                                    className="button"
                                    disabled={!checkRecordPerm()}
                                    intent="primary"
                                    leftIcon={<SaveIcon style={{ height: '20px' }} color="white" />}
                                    rightIcon={
                                        <Icon
                                            icon="chevronDown.medium"
                                            className={`text-white transition-transform duration-200 ${saveDropdownOpen ? '-rotate-180' : 'rotate-0'}`}
                                        />
                                    }
                                >
                                    Save
                                </Button>
                            }
                            options={saveOptions}
                            onOpenChange={() => setSaveDropdownOpen((prev) => !prev)}
                            onSelectOption={(option) => onSaveButton(option.id)}
                        />
                    </div>
                </div>
                <div className="body">
                    <div className="header">
                        <div className="leftDiv">
                            <Button
                                className="button"
                                shape="pill"
                                size="small"
                                rightIcon="chevronDown.medium"
                                active={dateButtonState.selected}
                                onClick={selectDate}
                            >
                                {dateButtonState.title}
                            </Button>
                            <Button
                                className="button"
                                disabled={!timeComparisonEnabled()}
                                shape="pill"
                                size="small"
                                rightIcon="chevronDown.medium"
                                active={comparisonButtonState.selected}
                                onClick={selectTimeComparison}
                            >
                                {comparisonButtonState.title}
                            </Button>
                        </div>
                        <div className="rightDiv">
                            <Button
                                className="button"
                                onClick={addMetric}
                                intent="secondary"
                                disabled={activeReportProxy.measures.length >= 4}
                                leftIcon="plus.medium"
                            >
                                Add
                            </Button>
                            <Button
                                className="button"
                                onClick={editMetrics}
                                intent="secondary"
                                leftIcon="pencil.small"
                            >
                                Edit
                            </Button>
                        </div>
                    </div>
                    <div className="metrics" style={{ width: '100%' }}>
                        <ReportMetricsPage
                            metrics={activeReportProxy.measures}
                            totals={tableDataState.columns}
                            dateType={activeReportProxy.dateType ?? ''}
                            dateCompareType={activeReportProxy.dateCompareType ?? ''}
                            activeMeasure={activeMetricState}
                            onSelect={(metric) => setActiveMetricState(metric.clone())} // clone to enforce re-render if the same metric is selected
                        />
                    </div>
                    <div className="visualizationSwitch">
                        <SegmentedControl
                            size="small"
                            segments={[
                                {
                                    label: 'Column',
                                    value: 'barchart',
                                    icon: <BarChartSwitchIcon style={{ height: '20px' }} />,
                                },
                                {
                                    label: 'Heat map',
                                    value: 'heatmap',
                                    icon: <HeatMapSwitchIcon style={{ height: '20px' }} />,
                                },
                            ]}
                            value={
                                activeVisualizationState === VisualizationType.BAR_CHART
                                    ? 'barchart'
                                    : 'heatmap'
                            }
                            onChange={() => {
                                setActiveVisualizationState((activeVisualization) => {
                                    return activeVisualization === VisualizationType.BAR_CHART
                                        ? VisualizationType.HEAT_MAP
                                        : VisualizationType.BAR_CHART
                                })
                            }}
                        />
                    </div>
                    <div className="visualization">
                        {activeVisualizationState === VisualizationType.BAR_CHART && (
                            <BarChart {...barChartDataState} />
                        )}
                        {activeVisualizationState === VisualizationType.HEAT_MAP && (
                            <HeatMap {...heatMapDataState} />
                        )}
                    </div>
                    <div className="groupBy">
                        <span className="text">Group by:</span>
                        <Button
                            className="button"
                            shape="pill"
                            rightIcon="chevronDown.medium"
                            active={group1ButtonState.selected}
                            onClick={() => selectGroupBy(true)}
                            size="small"
                        >
                            <span className="max-w-[200px] truncate">
                                {group1ButtonState.title}
                            </span>
                        </Button>
                        <span className="text">and</span>
                        <Button
                            className="button"
                            shape="pill"
                            rightIcon="chevronDown.medium"
                            active={group2ButtonState.selected}
                            onClick={() => selectGroupBy(false)}
                            size="small"
                        >
                            <span className="max-w-[200px] truncate">
                                {group2ButtonState.title}
                            </span>
                        </Button>
                    </div>
                    <div className="dataTable">
                        <ReportDataTable
                            measures={activeReportProxy.measures}
                            activeMeasure={activeMetricState}
                            reportResultRows={tableDataState.rows}
                            reportSubresultRows={tableDataState.subrows}
                            resultSorting={tableDataState.sorting}
                            groupByEligible={tableDataState.groupByEligible}
                            reportTotalColumns={tableDataState.columns}
                            showDetail={showReportDetails}
                        />
                    </div>
                </div>
            </div>

            {activeModal}
            {guardrailComponent}
        </AppHeader>
    )
}

export const ReportRunnerPage = usePSComponent<ReportRunnerPageProps>(render, viewWillAppear)
